import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Helpers from "../../Config/Helpers";
import { resolveLogoSrc } from "../../utils/resolveLogoSrc";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Moment from "react-moment";
import Select from "react-select";
import "./ReceiptDetails.css";
import { Box, Paper, Button } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PrintIcon from "@mui/icons-material/Print";
import PageShell from "../ModuleLayout/PageShell";
import { formatCustomerAddress } from "../../utils/customerAddress";
import { isIncludedGroupServicePart } from "../../utils/receiptCalculations";
import ReceiptEmailDialog from "./ReceiptEmailDialog";
import ReceiptPrint from "./ReceiptPrint";
import ReceiptEditHistory from "./ReceiptEditHistory";
import EditIcon from "@mui/icons-material/Edit";

function ReceiptDetails() {
  const [receipt, setReceipt] = useState({});
  const [shopNotes, setShopNotes] = useState("");
  const [shopNotesSaving, setShopNotesSaving] = useState(false);
  const { receipt_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const getReceipt = useCallback(() => {
    axios
      .get(`${Helpers.baseUrl}/receipts/details/${receipt_id}`, Helpers.headers)
      .then((response) => {
        setReceipt(response.data);
        setShopNotes(response.data?.shopNotes || "");
      })
      .catch((error) => Helpers.unauthenticated(error, navigate));
  }, [navigate, receipt_id]);

  useEffect(() => {
    getReceipt();
  }, [getReceipt]);

  const cur = (n) => (Number.isFinite(+n) ? `$ ${(+n).toFixed(2)}` : "$ 0.00");
  const getTechNames = (item = {}) => {
    const list = Array.isArray(item.technicians)
      ? item.technicians
      : item.technician
        ? [item.technician]
        : [];
    return list
      .map((t) => {
        if (typeof t === "string") return t;
        return (
          t?.label ||
          t?.name ||
          t?.full_name ||
          [t?.first_name, t?.last_name].filter(Boolean).join(" ") ||
          t?.username ||
          ""
        );
      })
      .filter(Boolean)
      .join(", ");
  };
  const formatPhone = (value) => {
    const digits = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    if (digits.length <= 3) return digits ? `(${digits}` : "";
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const formatEngineSize = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "-";
    return /l\b/i.test(raw) ? raw : `${raw} L`;
  };
  const formatDrivetrain = (value) => {
    const raw = String(value || "").trim();
    const normalized = raw.toLowerCase();
    if (!raw) return "-";
    if (normalized.includes("four") || normalized.includes("4wd") || normalized.includes("4x4")) return "4WD";
    if (normalized.includes("all") || normalized.includes("awd")) return "AWD";
    if (normalized.includes("front") || normalized.includes("fwd")) return "FWD";
    if (normalized.includes("rear") || normalized.includes("rwd")) return "RWD";
    if (
      normalized.includes("two") ||
      normalized.includes("2wd") ||
      normalized.includes("2-wheel") ||
      normalized.includes("2 wheel") ||
      normalized.includes("4x2")
    ) return "2WD";
    return raw;
  };
  const getDiscountAmount = () => {
    const stored = Number(receipt?.discountAmount);
    if (Number.isFinite(stored) && stored > 0) return stored;
    if (receipt?.discountType === "Percentage") {
      return ((Number(receipt?.subTotal || subTotal) || 0) * (Number(receipt?.discount) || 0)) / 100;
    }
    return Number(receipt?.discount) || 0;
  };
  const subTotal = useMemo(() => {
    const t = parseFloat(receipt?.totalPrice || 0);
    const tx = parseFloat(receipt?.tax || 0);
    return (t - (Number.isFinite(tx) ? tx : 0)).toFixed(2);
  }, [receipt]);
  const taxAmount = Number(receipt?.tax) || 0;
  const taxIsEnabled = ["true", "1", "yes"].includes(
    String(receipt?.taxInclude ?? receipt?.taxIncluded ?? "").toLowerCase(),
  );
  const shouldShowTax = taxIsEnabled || taxAmount > 0;
  const taxRateLabel = Number(receipt?.taxType) > 0 ? ` (${receipt.taxType}%)` : "";

  const services = (receipt?.services || []).filter(
    (service) => !isIncludedGroupServicePart(service),
  );
  const customer = receipt?.customer || {};
  const vehicle = receipt?.vehicle || {};
  const payments = receipt?.payments || [];
  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../../images/logo-light.png");

  const paymentTypeOptions = useMemo(
    () => [
      { label: "Cash", value: "Cash" },
      { label: "Card", value: "Card" },
      { label: "Check", value: "Check" },
      { label: "Online", value: "Online" },
    ],
    [],
  );
  const [paymentMethodOption, setPaymentMethodOption] = useState(null);
  const selectStyles = {
    control: (base) => ({ ...base, minHeight: 36, height: 36 }),
    valueContainer: (base) => ({ ...base, paddingTop: 4, paddingBottom: 4 }),
    indicatorsContainer: (base) => ({ ...base, height: 36 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const readableInvoiceNumber = useMemo(() => {
    if (receipt?.friendlyNumber) return receipt.friendlyNumber;
    if (receipt?.invoiceNumber) return String(receipt.invoiceNumber);
    if (receipt?._id) return `INV-${receipt._id.slice(-6).toUpperCase()}`;
    return "--";
  }, [receipt]);
  const isEstimate =
    String(receipt?.receiptType || "Quote").toLowerCase() === "quote";
  const detailsTitle = isEstimate ? "Estimate Details" : "Receipt Details";
  const sectionLabel = isEstimate ? "Estimates" : "Receipts";
  const numberLabel = isEstimate ? "Estimate No:" : "Invoice No:";
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const canEdit = !Helpers.authUser?.user_role || permissions.some((permission) =>
    permission.tab_link === (isEstimate ? "/user/estimates" : "/user/receipts") && String(permission.can_update) === "1");

  useEffect(() => {
    if (!receipt?.paymentType) {
      setPaymentMethodOption(null);
      return;
    }
    const initial = paymentTypeOptions.find(
      (opt) => opt.value === receipt.paymentType,
    );
    setPaymentMethodOption(initial || null);
  }, [paymentTypeOptions, receipt?.paymentType]);

  useEffect(() => {
    if (!receipt?._id) return;
    const params = new URLSearchParams(location.search);
    if (params.get("autoprint") !== "1") return;
    const timer = setTimeout(() => {
      window.print();
    }, 200);
    return () => clearTimeout(timer);
  }, [receipt?._id, location.search]);

  const handleUpdatePaymentMethod = () => {
    if (!paymentMethodOption) {
      Helpers.toast("error", "Please select a payment method");
      return;
    }
    axios
      .post(
        `${Helpers.baseUrl}/receipts/update-payment-type`,
        { receipt_id, payment_type: paymentMethodOption.value, expectedUpdatedAt: receipt.updatedAt },
        Helpers.headers,
      )
      .then(() => {
        Helpers.toast("success", "Payment method updated");
        getReceipt();
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
        Helpers.error_response(error);
      });
  };

  const handleUpdateShopNotes = () => {
    setShopNotesSaving(true);
    axios
      .post(
        `${Helpers.baseUrl}/receipts/update-shop-notes`,
        { receipt_id, shopNotes, expectedUpdatedAt: receipt.updatedAt },
        Helpers.headers,
      )
      .then((response) => {
        const savedNotes = response.data?.shopNotes || "";
        setReceipt((current) => ({ ...current, shopNotes: savedNotes, updatedAt: response.data.updatedAt || current.updatedAt }));
        setShopNotes(savedNotes);
        Helpers.toast("success", "Shop notes saved");
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
        Helpers.error_response(error);
      })
      .finally(() => setShopNotesSaving(false));
  };

  const statusBadgeClass =
    receipt.status === "Paid"
      ? "badge badge-soft-success fs-11"
      : receipt.status === "Unpaid"
        ? "badge badge-soft-danger fs-11"
        : "badge badge-soft-warning fs-11";

  return (
    <>
      <div className="screen-only">
        <PageShell
          title={detailsTitle}
          description={`${customer?.name ? `${customer.name} · ` : ""}${readableInvoiceNumber}`}
          icon={require("../../images/icons/receipt.png")}
          breadcrumbs={[
            {
              label: "Home",
              path: "/user/dashboard",
              icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
            },
            {
              label: sectionLabel,
              path: isEstimate ? "/user/estimates" : "/user/receipts",
            },
            {
              label: (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>
                    {readableInvoiceNumber !== "--"
                      ? readableInvoiceNumber
                      : detailsTitle}
                  </span>
                  {receipt?.status && (
                    <span
                      className={statusBadgeClass}
                      style={{ fontSize: 11, padding: "3px 8px" }}
                    >
                      ({receipt.status})
                    </span>
                  )}
                </Box>
              ),
              path: null,
            },
          ]}
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {canEdit && receipt._id && <Button variant="outlined" startIcon={<EditIcon />}
                onClick={() => navigate(`/user/receipts/edit/${receipt._id}`)} sx={{ textTransform: "none" }}>
                Edit {isEstimate ? "Estimate" : "Receipt"}
              </Button>}
              <ReceiptEmailDialog receipt={receipt} />
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                className="d-print-none"
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  backgroundColor: "#36589A",
                  borderRadius: 4,
                  padding: "4px 8px",
                  fontFamily: 'Inter, "Segoe UI", sans-serif',
                  "&:hover": {
                    backgroundColor: "#2E4D88",
                    color: "#FFFFFF !important",
                  },
                }}
              >
                Print
              </Button>
            </Box>
          }
          variant="page"
        >
          {/* ── Receipt Card ── */}
          <Paper
            elevation={1}
            sx={{ borderRadius: 2, mb: 2, overflow: "hidden" }}
            className="d-print-none"
          >
            <div className="row">
              <div className="col-lg-12">
                {/* Invoice header */}
                <div className="card-header border-bottom-dashed p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <img
                        src={shopLogoSrc}
                        className="card-logo card-logo-dark"
                        alt="logo dark"
                        height="50"
                      />
                      <img
                        src={shopLogoSrc}
                        className="card-logo card-logo-light"
                        alt="logo light"
                        height="17"
                      />
                      <div className="company-contact">
                        <div style={{ whiteSpace: "pre-line" }}>
                          {Helpers.authShop?.address?.replace(", ", ",\n")}
                        </div>
                        <div>{Helpers.authShop?.phone}</div>
                      </div>
                    </div>
                    <div className="text-end ms-3">
                      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                        <span className="text-muted text-uppercase fw-semibold">
                          {numberLabel}
                        </span>
                        <span className="fs-14 fw-semibold">
                          {readableInvoiceNumber}
                        </span>
                      </div>
                      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                        <span className="text-muted text-uppercase fw-semibold">
                          Date:
                        </span>
                        <span className="fs-14 fw-semibold">
                          {receipt?.date ? (
                            <Moment
                              date={receipt.date}
                              format="ddd MMM DD YYYY"
                            />
                          ) : (
                            "--"
                          )}
                        </span>
                      </div>
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <span className="text-muted text-uppercase fw-semibold">
                          Payment Status:
                        </span>
                        <span className={statusBadgeClass}>
                          {receipt?.status || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer & Vehicle */}
                <div className="card-body p-4 border-top border-top-dashed">
                  <div className="row g-3">
                    <div className="col-6">
                      <h6 className="text-muted text-uppercase fw-semibold mb-3">
                        Customer Details
                      </h6>
                      <p className="text-muted mb-1">
                        Name: {customer?.name || ""}
                      </p>
                      <p className="text-muted mb-1">
                        {formatCustomerAddress(customer) || ""}
                      </p>
                      <p className="text-muted mb-1">
                        Phone: {formatPhone(customer?.phone)}
                      </p>
                      <p className="text-muted mb-0">
                        Email: {customer?.email || ""}
                      </p>
                    </div>
                    <div className="col-6">
                      <h6 className="text-muted text-uppercase fw-semibold mb-3">
                        Vehicle Details
                      </h6>
                      <p className="text-muted mb-1">
                        Make: {vehicle?.make || ""}
                      </p>
                      <p className="text-muted mb-1">
                        Model: {vehicle?.model || ""}
                      </p>
                      <p className="text-muted mb-1">
                        Vin Number: {vehicle?.vin_number || ""}
                      </p>
                      <p className="text-muted mb-1">
                        Year: {vehicle?.year || ""}
                      </p>
                      {Number.isFinite(+receipt?.miles) && (
                        <p className="text-muted mb-1">
                          Mileage: {(+receipt.miles).toLocaleString()} mi
                        </p>
                      )}
                      <p className="text-muted mb-1">
                        Engine Size: {formatEngineSize(vehicle?.engine_size)}
                      </p>
                      <p className="text-muted mb-1">
                        Cylinders: {vehicle?.engine_cylinders || "-"}
                      </p>
                      <p className="text-muted mb-0">
                        Drivetrain: {formatDrivetrain(vehicle?.drivetrain)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Services table */}
                <div className="card-body p-4">
                  <div className="table-responsive">
                    <table className="table table-borderless text-center table-nowrap align-middle mb-0">
                      <thead>
                        <tr className="table-active">
                          <th scope="col">Service Details</th>
                          <th scope="col">Rate</th>
                          <th scope="col">Quantity</th>
                          <th scope="col" className="text-end">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((s, i) => {
                          const v = s?.value || s || {};
                          const techNames = getTechNames(v);
                          return (
                            <tr key={v?._id || i}>
                              <td className="text-start">
                                <span className="fw-medium">
                                  {s?.label || v?.name || v?.service_name}
                                </span>{" "}
                                <span className="text-muted">
                                  <small>
                                    {v?.tax === "Taxable"
                                      ? ""
                                      : "(Non Taxable)"}{" "}
                                    {v?.type === "tire_service"
                                      ? "Disposable Fee (2%)"
                                      : ""}
                                  </small>
                                </span>
                                <br />
                                <span className="text-muted">
                                  {v?.description}
                                </span>
                                {techNames && (
                                  <>
                                    <br />
                                    <span className="text-muted">
                                      <small>
                                        <strong>Tech:</strong> {techNames}
                                      </small>
                                    </span>
                                  </>
                                )}
                              </td>
                              <td>{cur(v?.price)}</td>
                              <td>{v?.quantity}</td>
                              <td className="text-end">
                                {cur(v?.total_price)}
                              </td>
                            </tr>
                          );
                        })}

                        {(receipt.tires || []).map((tire, i) => {
                          const t = tire?.value || tire || {};
                          return (
                            <tr key={t?._id || `tire-${i}`}>
                              <td className="text-start">
                                <span className="fw-medium">
                                  {(t.condition || t.quality || "").trim()}
                                  {t.condition || t.quality ? " - " : ""}
                                  {t.brand || ""} {t.size || ""}
                                </span>
                                <br />
                                <span className="text-muted">
                                  {t?.description || ""}
                                </span>
                              </td>
                              <td>{cur(t?.price)}</td>
                              <td>{Number(t?.quantity || 1)}</td>
                              <td className="text-end">
                                {cur(t?.total_price)}
                              </td>
                            </tr>
                          );
                        })}

                        {receipt.oil && (
                          <tr>
                            <td className="text-start">
                              <span className="fw-medium">
                                Oil: {receipt.oil.name} ({receipt.oil.brand})
                              </span>
                            </td>
                            <td>{cur(receipt.oilPrice)}</td>
                            <td>1</td>
                            <td className="text-end">
                              {cur(receipt.oilPrice)}
                            </td>
                          </tr>
                        )}

                        {Number(receipt.extraOilQuantity) > 0 && (
                          <tr>
                            <td className="text-start">
                              <span className="fw-medium">
                                Extra Oil: {receipt.extraOilQuantity} qt @ $
                                {Number(
                                  (receipt.extraOilPrice || 0) /
                                    (receipt.extraOilQuantity || 1),
                                ).toFixed(2)}{" "}
                                each
                              </span>
                            </td>
                            <td>{cur(receipt.extraOilPrice)}</td>
                            <td>{receipt.extraOilQuantity}</td>
                            <td className="text-end">
                              {cur(receipt.extraOilPrice)}
                            </td>
                          </tr>
                        )}

                        {receipt.oilFilter && (
                          <tr>
                            <td className="text-start">
                              <span className="fw-medium">
                                Filter: {receipt.oilFilter.name}
                                {receipt.oilFilter.type
                                  ? ` (${receipt.oilFilter.type})`
                                  : ""}
                              </span>
                            </td>
                            <td>{cur(receipt.oilFilterPrice)}</td>
                            <td>{Number(receipt.oilFilterQuantity || 1)}</td>
                            <td className="text-end">
                              {cur(receipt.oilFilterPrice)}
                            </td>
                          </tr>
                        )}

                        {receipt.fullServiceIncluded && (
                          <tr>
                            <td className="text-start">
                              <span className="fw-medium">
                                Full Service Included
                              </span>
                              <br />
                              <span className="text-muted">
                                TOP OFF ALL FLUIDS - COOLANT, WINDSHIELD WASHER,
                                POWER STEERING FLUID, BRAKE FLUID AND CHECK
                                TIRES PRESSURE
                              </span>
                            </td>
                            <td>{cur(receipt.fullServicePrice || 3.99)}</td>
                            <td>1</td>
                            <td className="text-end">
                              {cur(receipt.fullServicePrice || 3.99)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="border-top border-top-dashed mt-2">
                    <table className="table table-borderless table-nowrap align-middle mb-0 ms-auto">
                      <tbody>
                        <tr>
                          <td>Sub Total</td>
                          <td className="text-end">{cur(subTotal)}</td>
                        </tr>
                        {shouldShowTax && (
                          <tr>
                            <td>
                              Tax{taxRateLabel ? <small>{taxRateLabel}</small> : null}
                            </td>
                            <td className="text-end">{cur(receipt?.tax)}</td>
                          </tr>
                        )}
                        {String(receipt?.discountInclude) === "true" && (
                          <tr>
                            <td>Discount</td>
                            <td className="text-end">
                              - {cur(getDiscountAmount())}
                              {receipt?.discountType?.toLowerCase() ===
                              "percentage"
                                ? ` (${receipt?.discount}%)`
                                : ""}
                            </td>
                          </tr>
                        )}
                        <tr className="border-top border-top-dashed fs-15">
                          <th scope="row">Total Amount</th>
                          <th className="text-end">
                            {cur(receipt?.totalPrice)}
                          </th>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Tech Notes */}
                  <div className="col-12 mt-3">
                    <h6 className="text-muted text-uppercase fw-semibold mb-2">
                      Tech Notes
                    </h6>
                    <p className="text-muted mb-0">
                      {receipt?.techNotes || receipt?.tech_notes}
                    </p>
                  </div>

                  <div className="col-12 mt-3">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                      <h6 className="text-muted text-uppercase fw-semibold mb-0">
                        Shop Notes
                      </h6>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={handleUpdateShopNotes}
                        disabled={shopNotesSaving || !canEdit}
                      >
                        {shopNotesSaving ? "Saving..." : "Save Notes"}
                      </button>
                    </div>
                    <textarea
                      className="form-control"
                      rows={4}
                      value={shopNotes}
                      readOnly={!canEdit}
                      onChange={(event) => setShopNotes(event.target.value)}
                    />
                  </div>

                  {/* Payment details + history */}
                  <div className="row mt-3">
                    <div className="col">
                      <h6 className="text-muted text-uppercase fw-semibold mb-3">
                        Payment Details:
                      </h6>
                      <p className="text-muted mb-1">
                        Payment Status:{" "}
                        <span className="fw-medium">{receipt?.status}</span>
                      </p>
                      {receipt?.status !== "Unpaid" && (
                        <p className="text-muted mb-1">
                          Payment Method:{" "}
                          <span className="fw-medium">
                            {receipt?.paymentType}
                          </span>
                        </p>
                      )}
                      <p className="text-muted">
                        Total Amount:{" "}
                        <span className="fw-medium">
                          {cur(receipt?.totalPrice)}
                        </span>
                      </p>
                      <p className="text-muted">
                        Paid Amount:{" "}
                        <span className="fw-medium">{cur(receipt?.paid)}</span>
                      </p>
                      <p className="text-muted">
                        Remaining Amount:{" "}
                        <span className="fw-medium">
                          {cur(receipt?.remaining)}
                        </span>
                      </p>
                      {Number(receipt.overpaymentAmount) > 0 && <p className="text-muted">
                        {receipt.overpaymentDisposition === "customer_credit" ? "Customer credit" : "Refund due"}:{" "}
                        <span className="fw-medium">{cur(receipt.overpaymentAmount)}</span>
                      </p>}
                    </div>

                    {payments.map((p, i) => (
                      <div className="col" key={i}>
                        <h6 className="text-muted text-uppercase fw-semibold mb-3">
                          Payment History {i + 1}:
                        </h6>
                        <p className="text-muted mb-1">
                          Payment Date:{" "}
                          <span className="fw-medium">
                            <Moment
                              date={p.payment_date}
                              format="ddd MMM DD YYYY"
                            />
                          </span>
                        </p>
                        <p className="text-muted mb-1">
                          Payment Type:{" "}
                          <span className="fw-medium">{p.payment_type}</span>
                        </p>
                        <p className="text-muted mb-1">
                          Payment Due:{" "}
                          <span className="fw-medium">{cur(p.amount_due)}</span>
                        </p>
                        <p className="text-muted mb-1">
                          Payment Paid:{" "}
                          <span className="fw-medium">
                            {cur(p.amount_paid)}
                          </span>
                        </p>
                        <p className="text-muted">
                          Payment Remaining:{" "}
                          <span className="fw-medium">
                            {cur(p.amount_remaining)}
                          </span>
                        </p>
                        {(p.note || p.notes || p.payment_note) && (
                          <p className="text-muted mb-0">
                            Notes:{" "}
                            <span className="fw-medium">
                              {p.note || p.notes || p.payment_note}
                            </span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Update payment method */}
                  {canEdit && <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
                    <div
                      className="d-flex align-items-center gap-2 flex-grow-1"
                      style={{ maxWidth: 520 }}
                    >
                      <p
                        className="text-muted mb-0 text-uppercase fw-semibold"
                        style={{ minWidth: 130 }}
                      >
                        Payment Method
                      </p>
                      <Select
                        className="flex-grow-1"
                        options={paymentTypeOptions}
                        value={paymentMethodOption}
                        onChange={setPaymentMethodOption}
                        isClearable="true"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleUpdatePaymentMethod}
                      >
                        Update Method
                      </button>
                    </div>
                  </div>}
                </div>
              </div>
            </div>
            {receipt._id && <Box sx={{ px: 3, pb: 3 }}><ReceiptEditHistory receiptId={receipt._id} refreshKey={receipt.updatedAt} /></Box>}
          </Paper>
        </PageShell>
      </div>
      <ReceiptPrint receipt={receipt} shop={Helpers.authShop || {}} logoSrc={shopLogoSrc} />
    </>
  );
}

export default ReceiptDetails;
